import { topologyRoomBackedPlaneUVAABB } from "building-generation/elements/walls2/planarTopologySelectionGeometry";
import type { BuildingGraph, BuildingGraphPlanarTopology } from "building-types/building/buildingGraphTypes";
import {
  isAbsoluteVerticalDatum,
  isWallPlaneHorizontalDatum,
  type AbsoluteVerticalDatumEntity,
  type DatumEntity,
  type WallPlaneHorizontalDatumEntity,
} from "building-types/datums/datumEntityTypes";
import { applyDatumTagFilterForWallPlane, type DatumTagFilter } from "building-types/datums/datumTagFilterUtils";
import type { Doc } from "building-types/doc";
import type { BuildingGraphPlanarTopologyHandle } from "building-types/entities/buildingEntityHandle";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { PlanarTopologyDatumBounds } from "building-types/walls2/planarTopologyDatumBoundsTypes";
import type { Table } from "common/data/table";
import { DIST_EPSILON } from "geom/core";
import { worldZToTopologyV } from "./planarTopologyVerticalDatumProjection";

export interface PlanarTopologySplitRegion extends PlanarTopologyDatumBounds {
  readonly uMin: number;
  readonly uMax: number;
  readonly vMin: number;
  readonly vMax: number;
}

export interface HorizontalDatumInTopologyRange {
  readonly id: ForeignEntityId<"DATUM">;
  readonly offset: number;
}

export interface VerticalDatumInTopologyRange {
  readonly id: ForeignEntityId<"DATUM">;
  readonly offset: number;
}

export type WallPlaneHorizontalDatumForSplitRegions = Pick<WallPlaneHorizontalDatumEntity, "id" | "planeHash"> &
  Required<Pick<WallPlaneHorizontalDatumEntity, "offset">>;

export type ShareableVerticalDatumForSplitRegions = Pick<AbsoluteVerticalDatumEntity, "id"> &
  Required<Pick<AbsoluteVerticalDatumEntity, "offset">>;

interface PlanarTopologyUSplitStrip extends PlanarTopologyDatumBounds {
  readonly uMin: number;
  readonly uMax: number;
}

interface PlanarTopologyVSplitStrip extends PlanarTopologyDatumBounds {
  readonly vMin: number;
  readonly vMax: number;
}

export function wallPlaneHorizontalDatumsFromDatums(
  datums: Table<DatumEntity>,
): readonly WallPlaneHorizontalDatumForSplitRegions[] {
  const wallPlaneHorizontalDatums: WallPlaneHorizontalDatumForSplitRegions[] = [];
  for (const datumId in datums) {
    const datum = datums[datumId];
    if (!isWallPlaneHorizontalDatum(datum) || datum.offset === undefined) {
      continue;
    }
    wallPlaneHorizontalDatums.push({
      id: datumId as WallPlaneHorizontalDatumEntity["id"],
      planeHash: datum.planeHash,
      offset: datum.offset,
    });
  }
  return wallPlaneHorizontalDatums;
}

export function shareableVerticalDatumsFromDatums(
  datums: Table<DatumEntity>,
): readonly ShareableVerticalDatumForSplitRegions[] {
  const shareableVerticalDatums: ShareableVerticalDatumForSplitRegions[] = [];
  for (const datumId in datums) {
    const datum = datums[datumId];
    if (!isAbsoluteVerticalDatum(datum) || datum.offset === undefined) {
      continue;
    }
    shareableVerticalDatums.push({
      id: datumId as AbsoluteVerticalDatumEntity["id"],
      offset: datum.offset,
    });
  }
  return shareableVerticalDatums;
}

export function planarTopologyHandleWithDatumBounds(
  topologyId: string,
  bounds: PlanarTopologyDatumBounds = {},
): BuildingGraphPlanarTopologyHandle {
  return {
    entityType: "BUILDING_GRAPH_PLANAR_TOPOLOGY",
    id: topologyId,
    ...(bounds.startHorizontalDatumId !== undefined ? { startHorizontalDatumId: bounds.startHorizontalDatumId } : {}),
    ...(bounds.endHorizontalDatumId !== undefined ? { endHorizontalDatumId: bounds.endHorizontalDatumId } : {}),
    ...(bounds.startVerticalDatumId !== undefined ? { startVerticalDatumId: bounds.startVerticalDatumId } : {}),
    ...(bounds.endVerticalDatumId !== undefined ? { endVerticalDatumId: bounds.endVerticalDatumId } : {}),
  };
}

function collectHorizontalDatumsInteriorToURange(
  wallPlaneHorizontalDatums: readonly WallPlaneHorizontalDatumForSplitRegions[],
  planeHashValue: string,
  uMin: number,
  uMax: number,
): readonly HorizontalDatumInTopologyRange[] {
  const matches: HorizontalDatumInTopologyRange[] = [];
  for (const datum of wallPlaneHorizontalDatums) {
    if (datum.planeHash !== planeHashValue) {
      continue;
    }
    if (datum.offset <= uMin || datum.offset >= uMax) {
      continue;
    }
    matches.push({
      id: datum.id,
      offset: datum.offset,
    });
  }

  return matches.sort((a, b) => a.offset - b.offset);
}

function collectShareableVerticalDatumsInteriorToVRange(
  shareableVerticalDatums: readonly ShareableVerticalDatumForSplitRegions[],
  topology: BuildingGraphPlanarTopology,
  vMin: number,
  vMax: number,
): readonly VerticalDatumInTopologyRange[] {
  const matches: VerticalDatumInTopologyRange[] = [];
  for (const datum of shareableVerticalDatums) {
    const topologyV = worldZToTopologyV(topology, datum.offset);
    if (topologyV <= vMin || topologyV >= vMax) {
      continue;
    }
    matches.push({
      id: datum.id,
      offset: topologyV,
    });
  }

  return matches.sort((a, b) => a.offset - b.offset);
}

function findWallPlaneHorizontalDatumAtOffset(
  wallPlaneHorizontalDatums: readonly WallPlaneHorizontalDatumForSplitRegions[],
  planeHashValue: string,
  offset: number,
): WallPlaneHorizontalDatumForSplitRegions | undefined {
  for (const datum of wallPlaneHorizontalDatums) {
    if (datum.planeHash !== planeHashValue) {
      continue;
    }
    if (Math.abs(datum.offset - offset) < DIST_EPSILON) {
      return datum;
    }
  }
  return undefined;
}

function findShareableVerticalDatumIdAtOffset(
  shareableVerticalDatums: readonly ShareableVerticalDatumForSplitRegions[],
  topology: BuildingGraphPlanarTopology,
  offset: number,
): ForeignEntityId<"DATUM"> | undefined {
  for (const datum of shareableVerticalDatums) {
    const topologyV = worldZToTopologyV(topology, datum.offset);
    if (Math.abs(topologyV - offset) < DIST_EPSILON) {
      return datum.id;
    }
  }
  return undefined;
}

function computePlanarTopologyUSplitStripsForURange(
  uMin: number,
  uMax: number,
  datums: readonly HorizontalDatumInTopologyRange[],
): readonly PlanarTopologyUSplitStrip[] {
  if (uMin >= uMax) {
    return [];
  }

  if (datums.length === 0) {
    return [{ uMin, uMax }];
  }

  const strips: PlanarTopologyUSplitStrip[] = [];
  let previousOffset = uMin;

  for (let index = 0; index < datums.length; index++) {
    const datum = datums[index];
    const startDatum = index > 0 ? datums[index - 1] : undefined;
    strips.push({
      uMin: previousOffset,
      uMax: datum.offset,
      ...(startDatum !== undefined ? { startHorizontalDatumId: startDatum.id } : {}),
      endHorizontalDatumId: datum.id,
    });
    previousOffset = datum.offset;
  }

  const lastDatum = datums[datums.length - 1];
  strips.push({
    uMin: previousOffset,
    uMax,
    ...(lastDatum !== undefined ? { startHorizontalDatumId: lastDatum.id } : {}),
  });

  return strips;
}

function computePlanarTopologyVSplitStripsForVRange(
  vMin: number,
  vMax: number,
  datums: readonly VerticalDatumInTopologyRange[],
): readonly PlanarTopologyVSplitStrip[] {
  if (vMin >= vMax) {
    return [];
  }

  if (datums.length === 0) {
    return [{ vMin, vMax }];
  }

  const strips: PlanarTopologyVSplitStrip[] = [];
  let previousOffset = vMin;

  for (let index = 0; index < datums.length; index++) {
    const datum = datums[index];
    const startDatum = index > 0 ? datums[index - 1] : undefined;
    strips.push({
      vMin: previousOffset,
      vMax: datum.offset,
      ...(startDatum !== undefined ? { startVerticalDatumId: startDatum.id } : {}),
      endVerticalDatumId: datum.id,
    });
    previousOffset = datum.offset;
  }

  const lastDatum = datums[datums.length - 1];
  strips.push({
    vMin: previousOffset,
    vMax,
    ...(lastDatum !== undefined ? { startVerticalDatumId: lastDatum.id } : {}),
  });

  return strips;
}

function enrichUSplitStripsWithBoundaryDatums(
  strips: readonly PlanarTopologyUSplitStrip[],
  wallPlaneHorizontalDatums: readonly WallPlaneHorizontalDatumForSplitRegions[],
  planeHashValue: string,
): readonly PlanarTopologyUSplitStrip[] {
  if (strips.length === 0) {
    return strips;
  }

  const firstStrip = strips[0];
  const lastStrip = strips[strips.length - 1];

  let startDatum: WallPlaneHorizontalDatumForSplitRegions | undefined;
  if (firstStrip && firstStrip.startHorizontalDatumId === undefined) {
    startDatum = findWallPlaneHorizontalDatumAtOffset(wallPlaneHorizontalDatums, planeHashValue, firstStrip.uMin);
  }

  let endDatum: WallPlaneHorizontalDatumForSplitRegions | undefined;
  if (lastStrip && lastStrip.endHorizontalDatumId === undefined) {
    endDatum = findWallPlaneHorizontalDatumAtOffset(wallPlaneHorizontalDatums, planeHashValue, lastStrip.uMax);
  }

  return strips.map((strip, index) => {
    const isFirst = index === 0 && strip.startHorizontalDatumId === undefined;
    const isLast = index === strips.length - 1 && strip.endHorizontalDatumId === undefined;
    return {
      ...strip,
      ...(isFirst && startDatum ? { startHorizontalDatumId: startDatum.id } : {}),
      ...(isLast && endDatum ? { endHorizontalDatumId: endDatum.id } : {}),
    };
  });
}

function enrichVSplitStripsWithBoundaryDatums(
  strips: readonly PlanarTopologyVSplitStrip[],
  shareableVerticalDatums: readonly ShareableVerticalDatumForSplitRegions[],
  topology: BuildingGraphPlanarTopology,
): readonly PlanarTopologyVSplitStrip[] {
  if (strips.length === 0) {
    return strips;
  }

  const firstStrip = strips[0];
  const lastStrip = strips[strips.length - 1];

  let startDatumId: ForeignEntityId<"DATUM"> | undefined;
  if (firstStrip && firstStrip.startVerticalDatumId === undefined) {
    startDatumId = findShareableVerticalDatumIdAtOffset(shareableVerticalDatums, topology, firstStrip.vMin);
  }

  let endDatumId: ForeignEntityId<"DATUM"> | undefined;
  if (lastStrip && lastStrip.endVerticalDatumId === undefined) {
    endDatumId = findShareableVerticalDatumIdAtOffset(shareableVerticalDatums, topology, lastStrip.vMax);
  }

  return strips.map((strip, index) => {
    const isFirst = index === 0 && strip.startVerticalDatumId === undefined;
    const isLast = index === strips.length - 1 && strip.endVerticalDatumId === undefined;
    return {
      ...strip,
      ...(isFirst && startDatumId ? { startVerticalDatumId: startDatumId } : {}),
      ...(isLast && endDatumId ? { endVerticalDatumId: endDatumId } : {}),
    };
  });
}

export function combinePlanarTopologyUVSplitRegions(
  uStrips: readonly PlanarTopologyUSplitStrip[],
  vStrips: readonly PlanarTopologyVSplitStrip[],
): readonly PlanarTopologySplitRegion[] {
  if (uStrips.length === 0 || vStrips.length === 0) {
    return [];
  }

  const regions: PlanarTopologySplitRegion[] = [];
  for (const uStrip of uStrips) {
    for (const vStrip of vStrips) {
      regions.push({
        uMin: uStrip.uMin,
        uMax: uStrip.uMax,
        vMin: vStrip.vMin,
        vMax: vStrip.vMax,
        ...(uStrip.startHorizontalDatumId !== undefined
          ? { startHorizontalDatumId: uStrip.startHorizontalDatumId }
          : {}),
        ...(uStrip.endHorizontalDatumId !== undefined ? { endHorizontalDatumId: uStrip.endHorizontalDatumId } : {}),
        ...(vStrip.startVerticalDatumId !== undefined ? { startVerticalDatumId: vStrip.startVerticalDatumId } : {}),
        ...(vStrip.endVerticalDatumId !== undefined ? { endVerticalDatumId: vStrip.endVerticalDatumId } : {}),
      });
    }
  }

  return regions;
}

/** Split a wall segment UV span at wall-plane horizontal and shareable vertical datums. */
export function computePlanarTopologySplitRegionsForWallSegmentUV(
  wallPlaneHorizontalDatums: readonly WallPlaneHorizontalDatumForSplitRegions[],
  planeHashValue: string,
  segmentU: { readonly uMin: number; readonly uMax: number },
  segmentV: { readonly vMin: number; readonly vMax: number },
  shareableVerticalDatums: readonly ShareableVerticalDatumForSplitRegions[] = [],
  topology?: BuildingGraphPlanarTopology,
): readonly PlanarTopologySplitRegion[] {
  const interiorHorizontalDatums = collectHorizontalDatumsInteriorToURange(
    wallPlaneHorizontalDatums,
    planeHashValue,
    segmentU.uMin,
    segmentU.uMax,
  );
  const uStrips = computePlanarTopologyUSplitStripsForURange(segmentU.uMin, segmentU.uMax, interiorHorizontalDatums);
  const enrichedUStrips = enrichUSplitStripsWithBoundaryDatums(uStrips, wallPlaneHorizontalDatums, planeHashValue);

  const interiorVerticalDatums =
    topology !== undefined
      ? collectShareableVerticalDatumsInteriorToVRange(shareableVerticalDatums, topology, segmentV.vMin, segmentV.vMax)
      : [];
  const vStrips = computePlanarTopologyVSplitStripsForVRange(segmentV.vMin, segmentV.vMax, interiorVerticalDatums);
  const enrichedVStrips =
    topology !== undefined ? enrichVSplitStripsWithBoundaryDatums(vStrips, shareableVerticalDatums, topology) : vStrips;

  return combinePlanarTopologyUVSplitRegions(enrichedUStrips, enrichedVStrips);
}

export function computePlanarTopologySplitRegionsForDoc(
  doc: Pick<Doc, "datums">,
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
  datumTagFilter?: DatumTagFilter,
  activeStepDatumTag?: string,
  /** Plan-view pick regions split on horizontal datums only; elevation/3D include vertical datums. */
  includeShareableVerticalDatums: boolean = true,
): readonly PlanarTopologySplitRegion[] {
  const datums = applyDatumTagFilterForWallPlane(doc.datums, datumTagFilter, topology.planeHash, activeStepDatumTag);
  const uvAabb = topologyRoomBackedPlaneUVAABB(topology, buildingGraph, planLevelIds);
  if (!uvAabb) {
    return [];
  }

  const shareableVerticalDatums = includeShareableVerticalDatums ? shareableVerticalDatumsFromDatums(datums) : [];

  return computePlanarTopologySplitRegionsForWallSegmentUV(
    wallPlaneHorizontalDatumsFromDatums(datums),
    topology.planeHash,
    { uMin: uvAabb.minX, uMax: uvAabb.maxX },
    { vMin: uvAabb.minY, vMax: uvAabb.maxY },
    shareableVerticalDatums,
    topology,
  );
}
